/* eslint-disable semi, no-await-in-loop, no-restricted-syntax, no-restricted-globals, no-underscore-dangle, no-plusplus, no-console, no-use-before-define, no-param-reassign, no-continue */
/* ============================================================================
 * QA automation bridge — lets the admin panel's "Testing" page drive the REAL
 * app UI like a human (login, etc.), entirely client-side, with no extra server.
 *
 * How it works: the admin Testing page embeds this app in an iframe at
 *   https://projector.app/newSign-in?qa_bridge=1
 * This module (only when ?qa_bridge=1 is present) performs the real on-screen
 * actions — typing into inputs, clicking buttons — inside THIS app's own page
 * (same-origin to itself, so it's allowed), and reports each step back to the
 * admin page via postMessage. Turnstile passes because it's a real user browser.
 *
 * SAFETY:
 *  - Does nothing unless the URL has ?qa_bridge=1.
 *  - Only obeys messages from an allow-listed admin origin.
 *  - Holds NO credentials: the admin page supplies email/password per run.
 *  - Only performs actions a human could already do on the form; it never reads
 *    out tokens/cookies or exposes anything the page doesn't already show.
 * ========================================================================== */

// Deploy/version marker — on ANY load, `window.__pjQaBridge` holds this string.
// Check which bridge is loaded: open the app and run `window.__pjQaBridge` in
// the console. If it's undefined or an older stamp, you're on a stale bundle.
const QA_BRIDGE_VERSION = '2026-10-29b · + play-video test'
try { window.__pjQaBridge = QA_BRIDGE_VERSION } catch { /* noop */ }

// The email of the account the signup flow created this page-load. Persisted to
// localStorage on signup SUCCESS so a later test (trial-upload) can LOG IN to the
// SAME account instead of creating a second one — the handoff survives the iframe
// reload between tests because both run on this product origin. The key doesn't
// match clearAuthForFreshSession's token/auth/session pattern, so it isn't wiped.
const QA_SIGNUP_EMAIL_KEY = 'pj_qa_signup_email'
let lastSignupEmail = ''
function storedSignupEmail() {
  try { return window.localStorage.getItem(QA_SIGNUP_EMAIL_KEY) || '' } catch { return '' }
}
function rememberSignupEmail(email) {
  try { if (email) window.localStorage.setItem(QA_SIGNUP_EMAIL_KEY, email) } catch { /* noop */ }
}

const ALLOWED_ORIGINS = [
  'http://localhost:5173',
  'http://localhost:4173',
  'http://127.0.0.1:5173',
  'https://admin-projector.eq7.in',
  'https://admin.projectorstream.com',
]
// Any localhost origin is allowed too, so local dev (admin on :5173, app on
// :3000) works without deploying and regardless of the admin's dev port.
const LOCAL_ORIGIN = /^https?:\/\/(localhost|127\.0\.0\.1)(:\d+)?$/
function isAllowedOrigin(origin) {
  return ALLOWED_ORIGINS.includes(origin) || LOCAL_ORIGIN.test(origin)
}

/* ---------- tiny DOM helpers (human-like) ---------- */
const sleep = (ms) => new Promise((r) => setTimeout(r, ms))
const isVisible = (el) => !!el && !!el.offsetParent && el.getClientRects().length > 0

/* ---- verbose logging so a console copy is enough to debug ---- */
function qlog(...a) { try { console.log('%c[qa]', 'color:#3ECF8E;font-weight:bold', ...a) } catch (_) { /* noop */ } }
function describe(el) {
  if (!el) return 'null'
  const label = (el.value || el.textContent || '').trim().replace(/\s+/g, ' ').slice(0, 22)
  const cls = (el.className || '').toString().replace(/\s+/g, '.').slice(0, 28)
  return `<${el.tagName.toLowerCase()}${el.type ? ` ${el.type}` : ''}${el.placeholder ? ` ph="${el.placeholder}"` : ''}${label ? ` "${label}"` : ''}${el.disabled ? ' DISABLED' : ''}${cls ? ` .${cls}` : ''}>`
}
function snap(label) {
  const vis = (el) => !!el.offsetParent && !/hiddenFormField/i.test(el.className || '')
  const inputs = Array.from(document.querySelectorAll('input, select')).filter(vis).map(describe)
  const buttons = Array.from(document.querySelectorAll('button, input[type="submit"], input[type="button"], [role="button"]')).filter(vis).map(describe)
  qlog(`▼ ${label}  (url=${location.pathname})`)
  qlog('   INPUTS :', inputs.length ? inputs.join('   ') : '(none)')
  qlog('   BUTTONS:', buttons.length ? buttons.join('   ') : '(none)')
}

async function waitFor(getter, { timeout = 15000, interval = 200 } = {}) {
  const end = Date.now() + timeout
  for (;;) {
    let v
    try { v = getter() } catch { v = null }
    if (v) return v
    if (Date.now() >= end) throw new Error('timeout')
    await sleep(interval)
  }
}

function visibleByPlaceholder(ph) {
  return Array.from(document.querySelectorAll(`input[placeholder="${ph}"]`)).find(isVisible) || null
}

// The visible password field — by type, since its placeholder is localized
// (signInText.enterYourPassword) and not a fixed string. Skips the hidden
// name="password" mirror (which isn't visible) and the email field.
function passwordInput() {
  // Skip the hidden mirror field (class hiddenFormField) that looks "visible".
  const real = (el) => isVisible(el) && !/hiddenFormField|hidden/i.test(el.className || '')
  const byType = Array.from(document.querySelectorAll('input[type="password"]')).find(real)
  if (byType) return byType
  return Array.from(document.querySelectorAll('input')).find(
    (el) => real(el) && el.type !== 'email'
      && /password/i.test(`${el.placeholder || ''} ${el.getAttribute('aria-label') || ''} ${el.id || ''}`),
  ) || null
}
// A visible, truly-enabled button whose label matches `reInclude` (and not
// `reExclude`). Handles both the `disabled` property and `aria-disabled`.
function buttonLabel(b) {
  return `${b.textContent || ''} ${b.value || ''} ${b.getAttribute('aria-label') || ''}`.trim()
}
const isEnabled = (b) => isVisible(b) && !b.disabled && b.getAttribute('aria-disabled') !== 'true'

// The sign-in form's primary CTA (email "Continue" and password "Continue" both
// carry styles.primaryAction; CRA keeps that name in the hashed class). This is
// the ONE button we ever click — never the Google/Apple social buttons, which
// live in a different container and whose label happens to contain "Continue".
function submitButton() {
  const byClass = Array.from(document.querySelectorAll('[class*="primaryAction"]')).find(isEnabled)
  if (byClass) return byClass
  // Fallback if the module class ever changes: an enabled Continue/Get Code/
  // Signing-In button that is explicitly NOT a social ("…with Google/Apple").
  return Array.from(document.querySelectorAll('button, input[type="submit"], [role="button"]')).find((b) => {
    if (!isEnabled(b)) return false
    const t = buttonLabel(b)
    if (/google|apple|\bwith\b|sign ?up/i.test(t)) return false
    return /^(continue|get code|signing in)/i.test(t)
  }) || null
}

/* ---- signup helpers (buttons here are <input type="submit"> with value=label) ---- */
function visibleInputByType(type) {
  return Array.from(document.querySelectorAll(`input[type="${type}"]`)).find(isVisible) || null
}
function submitBy(reLabel, reExclude) {
  const nodes = document.querySelectorAll('input[type="submit"], button[type="submit"], button')
  return Array.from(nodes).find((b) => {
    if (!isEnabled(b)) return false
    const label = `${b.value || ''} ${b.textContent || ''} ${b.getAttribute('aria-label') || ''}`.trim()
    if (/google|apple/i.test(label)) return false
    if (reExclude && reExclude.test(label)) return false
    return reLabel.test(label)
  }) || null
}
// A visible, enabled clickable (button OR <div role="button"> OR link) whose
// text / aria-label matches — the onboarding screens (upload grid, device list,
// invite) use <div role="button"> CTAs that submitBy() can't see.
function clickableByText(reLabel, reExclude) {
  const nodes = document.querySelectorAll('button, input[type="submit"], input[type="button"], [role="button"], a')
  return Array.from(nodes).find((b) => {
    if (!isVisible(b) || b.disabled || b.getAttribute('aria-disabled') === 'true') return false
    const label = `${b.value || ''} ${b.textContent || ''} ${b.getAttribute('aria-label') || ''}`.replace(/\s+/g, ' ').trim()
    if (/google|apple/i.test(label)) return false
    if (reExclude && reExclude.test(label)) return false
    return reLabel.test(label)
  }) || null
}
function nameFieldInputs() {
  // Prefer placeholder match (First Name / Last Name), in order.
  const first = Array.from(document.querySelectorAll('input')).find((i) => isVisible(i) && /first\s*name/i.test(i.placeholder || ''))
  const last = Array.from(document.querySelectorAll('input')).find((i) => isVisible(i) && /last\s*name/i.test(i.placeholder || ''))
  if (first && last) return [first, last]
  const wrap = document.querySelector('[class*="nameFields" i]')
  if (wrap) return Array.from(wrap.querySelectorAll('input')).filter(isVisible)
  return []
}
function otpBoxes() {
  return Array.from(document.querySelectorAll('input[class*="codeInput"]')).filter(isVisible)
}
function setSelectValue(sel, value) {
  const setter = Object.getOwnPropertyDescriptor(window.HTMLSelectElement.prototype, 'value').set
  setter.call(sel, value)
  sel.dispatchEvent(new Event('change', { bubbles: true }))
}
function randomYopmail() {
  return `qa${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}@yopmail.com`
}
// Type a number into a react-phone-number-input: pick the country from the E.164
// prefix (so the national digits validate) and type the rest.
async function fillPhone(tel, e164) {
  const select = document.querySelector('select.PhoneInputCountrySelect')
  let national = e164
  if (select && e164.startsWith('+91')) { setSelectValue(select, 'IN'); national = e164.slice(3) }
  await humanType(tel, national)
}
// Surface a validation / Firebase error the app rendered (snackbar or inline),
// so a failure reads as the real reason instead of a generic timeout.
function pageErrorText() {
  const body = document.body ? document.body.innerText : ''
  const m = body.match(/invalid[- ]app[- ]credential|too many (?:attempts|requests)|quota (?:for this|exceeded)[^\n]*|recaptcha[^\n]*|verification failed[^\n]*|valid (?:mobile|phone) number[^\n]*|password must[^\n]*|is required[^\n]*|already (?:exists|registered)[^\n]*|network (?:error|request failed)[^\n]*/i)
  return m ? m[0].trim().replace(/\s+/g, ' ').slice(0, 140) : ''
}
function visibleButtonsDump() {
  return Array.from(document.querySelectorAll('button, input[type="submit"], input[type="button"], [role="button"]'))
    .filter(isVisible)
    .map((b) => `"${buttonLabel(b).slice(0, 24)}"${b.disabled ? '(disabled)' : ''}`)
    .slice(0, 10)
    .join(', ')
}

function pressEnter(el) {
  if (!el) return
  el.focus()
  const opts = { key: 'Enter', code: 'Enter', keyCode: 13, which: 13, bubbles: true }
  el.dispatchEvent(new KeyboardEvent('keydown', opts))
  el.dispatchEvent(new KeyboardEvent('keyup', opts))
}
function pageHasText(re) {
  return re.test(document.body ? document.body.innerText : '')
}

// React tracks the input's value internally; set it via the native setter and
// dispatch a real input event so React's onChange fires.
function setNativeValue(el, value) {
  const proto = el instanceof window.HTMLTextAreaElement ? window.HTMLTextAreaElement.prototype : window.HTMLInputElement.prototype
  const setter = Object.getOwnPropertyDescriptor(proto, 'value').set
  setter.call(el, value)
  el.dispatchEvent(new Event('input', { bubbles: true }))
}
async function humanType(el, text) {
  qlog('⌨  type into', describe(el), '=', JSON.stringify(String(text).slice(0, 30)))
  el.focus()
  setNativeValue(el, '')
  let acc = ''
  for (const ch of String(text)) {
    acc += ch
    setNativeValue(el, acc)
    await sleep(35)
  }
  el.dispatchEvent(new Event('change', { bubbles: true }))
  if (typeof el.blur === 'function') el.blur()
}

function requireField(v, name) {
  if (!v) throw new Error(`No ${name} supplied by the admin page.`)
  return v
}

const FIREBASE_HINT = "OTP send likely failed at Firebase (console shows 'sendVerificationCode 400'). Fix in Firebase Console: add the phone as a TEST number (Auth → Phone → numbers for testing) and add this domain to Authorized domains."

// Start every QA run from a logged-out session so the sign-in form actually
// shows (otherwise a token left over from a previous run keeps us "logged in").
// Targeted: only auth-ish keys, so we don't wipe unrelated state. Runs at init,
// before React reads the token, so the app boots signed out.
function clearAuthForFreshSession() {
  const EXACT = ['token', 'uploader_token', 'uploader_token_expiry', 'user_uuid']
  try {
    const ls = window.localStorage
    for (const k of EXACT) ls.removeItem(k)
    for (const k of Object.keys(ls)) {
      if (/token|auth|session|logged|uuid/i.test(k)) ls.removeItem(k)
    }
    // Pin the UI design for the run so it's deterministic (booting logged-out,
    // getConfig otherwise resolves a generic design that may flip between runs).
    // The admin Testing page's UI toggle sends ?ui=classic|new; default classic.
    // `ui.classic` beats the default in resolveNewUI(), set before bootUiConfig
    // locks the design.
    let uiParam = 'classic'
    try { uiParam = new URLSearchParams(window.location.search).get('ui') || 'classic' } catch { /* noop */ }
    ls.setItem('ui.classic', uiParam === 'new' ? 'false' : 'true')
  } catch { /* storage blocked */ }
  try { window.sessionStorage.clear() } catch { /* noop */ }
}

/* ---------- upload helpers ---------- */
function bodyText() { return document.body ? document.body.innerText : '' }
// Is this run on the NEW UI? The bridge pins the design at init from ?ui=, so
// read that pin. Classic = the header-"Upload Videos" modal flow; new = the
// dashboard "Choose Local Files" direct-pipeline flow (no metadata modal).
function isNewUIRun() {
  try { return window.localStorage.getItem('ui.classic') === 'false' } catch { return false }
}
// The no-plan "upgrade" / block popup shown when a user without an active plan
// tries to upload: UploadBlockerPopup (default "Start your free trial" + limit
// "reached your trial limit" variants) or the legacy TrailpopUp promo — or a
// redirect to a plans / purchase-plan page. Returns a short reason, or '' if none.
function upgradeBlockShown() {
  try {
    if (/\/purchase-?plan|\/switchplan|\/plans(\/|$)/i.test(location.pathname)) return `redirected to ${location.pathname}`
  } catch (_) { /* noop */ }
  const t = bodyText()
  if (/start your free trial|start your 14-day free trial|continue as viewer|reached your trial limit/i.test(t)) return 'upgrade popup'
  if (/unlock the ultimate personal|subscribe to unlock projector/i.test(t)) return 'trial promo popup'
  return ''
}
// The dashboard header's Upload button (classic: global ownavUploadWrap, else an
// enabled button whose exact text is "Upload"). New UI has no header upload icon.
function uploadTrigger() {
  return document.querySelector('button.ownavUploadWrap')
    || clickableByText(/^\s*upload\s*$/i, /photo|story|phone|drive|dropbox|videos?|record/i)
}
// The video <input type=file> inside VideoUploadModal (accept list, NO video/*).
function videoFileInput() {
  return document.querySelector('input[type="file"][accept*=".mp4"]:not([accept*="video/"])')
    || document.querySelector('input[type="file"][accept*=".mp4"]')
}
// The dashboard "Choose Local Files" hidden input (accept includes video/*,image/*)
// — the new-UI path, which uploads straight to the pipeline (no modal). Setting
// files on it directly fires its onChange (we never click it, to avoid an OS dialog).
function localFilesInput() {
  return document.querySelector('input[type="file"][accept*="video/"]')
}
// The file input to drop videos onto for this run's UI.
function uploadInput() {
  return isNewUIRun() ? localFilesInput() : videoFileInput()
}
// The specific dialog paper that holds the upload modal (single or multi),
// identified by its own markers — so we don't act on some other dialog/menu.
function uploadDialogRoot() {
  const marker = videoFileInput()
    || document.querySelector('.mvu-root')
    || Array.from(document.querySelectorAll('h2, h1, [id="mvu-heading"]'))
      .find((h) => isVisible(h) && /select video to upload|upload video details|review & publish/i.test(h.textContent || ''))
  if (!marker) return null
  return marker.closest('.MuiDialog-container, .MuiPaper-root, [role="dialog"]') || marker.parentElement
}
// Is an upload modal still on screen?
function uploadModalOpen() { return !!uploadDialogRoot() }
// The modal's own close (X) — a MUI IconButton wrapping a CloseIcon, scoped to
// the upload dialog (the page header has its own CloseIcons we must not click).
function modalCloseButton() {
  const root = uploadDialogRoot()
  if (!root) return null
  const icon = root.querySelector('svg[data-testid="CloseIcon"]')
  if (icon && icon.closest('button')) return icon.closest('button')
  return Array.from(root.querySelectorAll('button'))
    .find((b) => isVisible(b) && (b.querySelector('svg[data-testid="CloseIcon"]') || /^\s*(close|cancel)\s*$/i.test(b.textContent || ''))) || null
}
// After publishing, the app should close the modal itself; if it doesn't (an
// exception in its publish handler leaves it open, now showing the empty "Select
// Video to Upload" dropzone), dismiss it ourselves. The upload is already fired
// in the background by then.
async function ensureUploadModalClosed() {
  try {
    await waitFor(() => !uploadModalOpen(), { timeout: 10000 })
    return 'modal closed'
  } catch (_) {
    /* still open — force it closed below */
  }
  for (let attempt = 0; attempt < 3 && uploadModalOpen(); attempt++) {
    const x = modalCloseButton()
    if (x) {
      qlog(`modal still open — click close (attempt ${attempt + 1}) →`, describe(x))
      x.click()
    } else {
      // No X found — press Escape on the dialog (MUI closes via onClose).
      const root = uploadDialogRoot()
      if (root) root.dispatchEvent(new KeyboardEvent('keydown', { key: 'Escape', code: 'Escape', keyCode: 27, which: 27, bubbles: true }))
    }
    await waitFor(() => !uploadModalOpen(), { timeout: 4000 }).catch(() => {})
  }
  return uploadModalOpen() ? 'modal still open' : 'modal closed (forced)'
}
// A dropdown/menu item by text (the Upload menu is MUI MenuList #composition-menu).
function menuItemByText(re) {
  const scope = document.querySelector('#composition-menu') || document
  return Array.from(scope.querySelectorAll('li, [role="menuitem"], [class*="menuItem"], [class*="menuText"]'))
    .find((el) => isVisible(el) && re.test(el.textContent || '')) || null
}
// An open MUI popover option (Select menu / listbox) by text.
function optionByText(re) {
  return Array.from(document.querySelectorAll('li[role="option"], li.MuiMenuItem-root, [role="option"], [role="menuitem"]'))
    .find((el) => isVisible(el) && re.test(el.textContent || '')) || null
}
// MUI <Select> opens on mousedown, not click — dispatch that to open the menu.
function openMuiSelect(el) {
  if (!el) return
  try { el.focus() } catch (_) { /* noop */ }
  el.dispatchEvent(new MouseEvent('mousedown', { bubbles: true, cancelable: true }))
}
// A small, visible element whose (short) text matches — for clickable <div>/<span>
// tabs/toggles that carry an onClick but no role (e.g. "Create Custom",
// "Additional Options"). Clicking it bubbles to the handler on its parent.
function elByText(re) {
  return Array.from(document.querySelectorAll('button, [role="button"], a, li, div, span, h3, p'))
    .find((el) => {
      if (!isVisible(el)) return false
      const txt = (el.textContent || '').replace(/\s+/g, ' ').trim()
      return txt.length > 0 && txt.length < 44 && re.test(txt)
    }) || null
}
// The clickable trigger of a MUI <Select> sitting under a <label> with this text
// (the modal's Category / Visibility selects render as a div, not a <select>).
function muiSelectByLabel(labelText) {
  const re = new RegExp(`^\\s*${labelText}\\s*$`, 'i')
  const lab = Array.from(document.querySelectorAll('label, h3, span, div'))
    .find((l) => isVisible(l) && re.test((l.textContent || '').trim()))
  if (!lab) return null
  let el = lab
  for (let hops = 0; hops < 4 && el; hops++) {
    const sel = el.querySelector && el.querySelector('.MuiSelect-select, [role="combobox"], [role="button"].MuiSelect-select')
    if (sel && isVisible(sel)) return sel
    el = el.parentElement
  }
  return null
}
// Is the multi-video modal open? New UI = .mvu-root / "Review & publish";
// classic = MultipleVideoUploadModalClassic ("Add More Videos" / "...published to").
function multiModalOpen() {
  return !!document.querySelector('.mvu-root') || /review & publish|add more videos|will be published to/i.test(bodyText())
}
// The per-video title inputs in the multi modal. New UI ids are mvu-title-*;
// classic has visible text inputs (excluding the MUI-v4 select's hidden native input).
function multiTitleInputs() {
  const neu = Array.from(document.querySelectorAll('input[id^="mvu-title-"]')).filter(isVisible)
  if (neu.length) return neu
  return Array.from(document.querySelectorAll('input')).filter((i) => isVisible(i)
    && (i.type === 'text' || i.type === '' || !i.type)
    && !/nativeInput/i.test(i.className || '')
    && !/PhoneInput/i.test(i.className || ''))
}
// The multi modal's publish button: "Publish 3 videos" (new) or "Publish"
// (classic) — but NOT the single modal's "Publish Video".
function multiPublishButton() {
  const neu = document.querySelector('.mvu-btn-primary')
  if (neu && isEnabled(neu)) return neu
  return Array.from(document.querySelectorAll('button')).find((b) => isEnabled(b)
    && /^\s*publish(\s+\d+\s+videos?)?\s*$/i.test((b.textContent || '').trim())) || null
}
// Set File(s) on an <input type=file> the way a human picker would, so React's
// onChange fires (DataTransfer is the only way to populate input.files).
function setFilesOnInput(input, files) {
  const dt = new DataTransfer()
  for (const f of files) dt.items.add(f)
  input.files = dt.files
  input.dispatchEvent(new Event('input', { bubbles: true }))
  input.dispatchEvent(new Event('change', { bubbles: true }))
}
// Build a REAL, decodable short clip (~targetBytes) in-browser. The upload modal
// generates thumbnails by decoding the file in a <video>, so a random blob would
// never let Publish enable — this records an animated canvas via MediaRecorder
// (mp4 when supported, else webm, which handleFileSelect also accepts).
async function makeVideoFile(targetBytes, baseName) {
  if (typeof window.MediaRecorder === 'undefined') throw new Error('MediaRecorder unavailable — cannot synthesize a test video.')
  const canvas = document.createElement('canvas')
  // Frame size × duration drive the encoded size for noisy (incompressible)
  // content; these land the clip near ~2MB (640×360/3s overshot to ~6MB).
  canvas.width = 384; canvas.height = 216
  const ctx = canvas.getContext('2d')
  const stream = canvas.captureStream(24)
  const prefer = ['video/mp4;codecs=avc1', 'video/mp4', 'video/webm;codecs=vp8', 'video/webm']
  const mime = prefer.find((m) => { try { return MediaRecorder.isTypeSupported(m) } catch { return false } }) || 'video/webm'
  const durationMs = 2500
  const bps = Math.max(1000000, Math.round((targetBytes * 8) / (durationMs / 1000)))
  const rec = new MediaRecorder(stream, { mimeType: mime, videoBitsPerSecond: bps })
  const chunks = []
  rec.ondataavailable = (e) => { if (e.data && e.data.size) chunks.push(e.data) }
  const stopped = new Promise((res) => { rec.onstop = res })
  rec.start()
  // High-entropy noise each frame so the encoder can't over-compress — keeps the
  // clip near the requested size (a flat animation collapsed to ~0.4MB).
  const noise = ctx.createImageData(canvas.width, canvas.height)
  const t0 = Date.now()
  await new Promise((resolve) => {
    const draw = () => {
      const t = Date.now() - t0
      const d = noise.data
      for (let i = 0; i < d.length; i += 4) { d[i] = Math.random() * 256; d[i + 1] = Math.random() * 256; d[i + 2] = Math.random() * 256; d[i + 3] = 255 }
      ctx.putImageData(noise, 0, 0)
      ctx.fillStyle = '#fff'; ctx.font = 'bold 42px sans-serif'
      ctx.fillText(`QA ${t}ms`, 40, 190 + 60 * Math.sin(t / 180))
      if (t < durationMs) requestAnimationFrame(draw); else resolve()
    }
    draw()
  })
  rec.stop()
  await stopped
  try { stream.getTracks().forEach((tr) => tr.stop()) } catch (_) { /* noop */ }
  const type = mime.split(';')[0]
  const ext = type === 'video/mp4' ? 'mp4' : 'webm'
  const blob = new Blob(chunks, { type })
  return new File([blob], `${baseName}-${Date.now()}.${ext}`, { type })
}

// Shared upload steps (used by the single- and multi-video flows after login).
const goToDashboardStep = {
  title: 'Open dashboard',
  hint: 'navigate to /dashboard',
  run: async () => {
    if (!/\/dashboard/.test(location.pathname)) {
      // SPA navigate (pushState + popstate) — a full reload would re-init the
      // bridge and wipe the just-obtained auth token.
      try { window.history.pushState({}, '', '/dashboard'); window.dispatchEvent(new PopStateEvent('popstate')) } catch (_) { /* noop */ }
      try {
        await waitFor(() => /\/dashboard/.test(location.pathname), { timeout: 4000 })
      } catch {
        // Fallback: click a real dashboard link so React Router navigates.
        const link = document.querySelector('a[href="/dashboard"], a[href$="/dashboard"]')
        if (link) { qlog('click dashboard link →', describe(link)); link.click() }
      }
    }
    await waitFor(() => /\/dashboard/.test(location.pathname), { timeout: 15000 })
    // Classic: wait for the header Upload button. New UI: wait for the dashboard
    // "Choose Local Files" input (no header upload icon exists there).
    await waitFor(() => (isNewUIRun() ? localFilesInput() : uploadTrigger()), { timeout: 20000 })
    return `on ${location.pathname} (${isNewUIRun() ? 'new UI' : 'classic'})`
  },
}
const openUploaderStep = {
  title: 'Open uploader',
  hint: 'Upload → Upload Videos (classic)',
  run: async () => {
    if (isNewUIRun()) {
      // New UI has no header upload icon; the dashboard's "Choose Local Files"
      // uploads straight to the pipeline (no metadata modal). Confirm its input.
      await waitFor(localFilesInput, { timeout: 15000 })
      return 'new UI · dashboard upload (direct, no modal)'
    }
    const btn = await waitFor(() => { const b = uploadTrigger(); return (b && isEnabled(b)) ? b : null }, { timeout: 20000 })
    qlog('click Upload →', describe(btn)); btn.click()
    const item = await waitFor(() => menuItemByText(/upload videos/i), { timeout: 10000 })
    qlog('click Upload Videos →', describe(item)); item.click()
    await waitFor(() => videoFileInput() || /select video to upload|upload video details/i.test(bodyText()), { timeout: 12000 })
    return 'uploader open'
  },
}
function makeVerifyStep(n) {
  return {
    title: 'Verify upload',
    hint: 'UploadPanel → completed',
    run: async () => {
      // After Publish the modal closes and the global UploadPanel reports
      // progress; completion flips "Uploading N" → "Uploaded N" / "N completed".
      const done = () => /Uploaded \d+ file|[1-9]\d* completed/i.test(bodyText())
      const end = Date.now() + 120000
      while (Date.now() < end) {
        if (done()) return `upload completed (${n} video${n > 1 ? 's' : ''})`
        if (pageHasText(/upload (failed|error)|couldn'?t upload|something went wrong/i)) throw new Error('The upload panel reported a failure.')
        await sleep(1000)
      }
      if (/\/your-videos/.test(location.pathname)) return `published → ${location.pathname} (still finishing)`
      throw new Error(`No completion signal within 120s (on ${location.pathname}).`)
    },
  }
}

// A profile video tile — both UIs render <a id="thumbnail-link" href="/video/:id">.
function videoTile() {
  return Array.from(document.querySelectorAll('a#thumbnail-link, a[href^="/video/"], a[href*="/video/"]'))
    .find((a) => isVisible(a)) || null
}
// Navigate to /profile (SPA push, like the dashboard step) and wait for the video
// grid to render at least one tile.
const goToProfileStep = {
  title: 'Open profile',
  hint: 'navigate to /profile',
  run: async () => {
    if (!/\/profile/.test(location.pathname)) {
      try { window.history.pushState({}, '', '/profile'); window.dispatchEvent(new PopStateEvent('popstate')) } catch (_) { /* noop */ }
      try {
        await waitFor(() => /\/profile/.test(location.pathname), { timeout: 4000 })
      } catch {
        const link = document.querySelector('a[href="/profile"], a[href$="/profile"]')
        if (link) { qlog('click profile link →', describe(link)); link.click() }
      }
    }
    await waitFor(() => /\/profile/.test(location.pathname), { timeout: 15000 })
    await waitFor(videoTile, { timeout: 25000 })
    return `on ${location.pathname} (${isNewUIRun() ? 'new UI' : 'classic'})`
  },
}

/* ---------- flows ---------- */
// Each step: { title, hint, run(cfg) } — returns a detail string or throws.
const loginSteps = [
      {
        title: 'Open sign-in page',
        hint: 'wait for form',
        run: async () => {
          await waitFor(() => pageHasText(/Welcome back\. Sign in to continue to your Projector\./) || visibleByPlaceholder('Email'), { timeout: 25000 })
          return `loaded ${location.pathname}`
        },
      },
      {
        title: 'Enter email',
        hint: 'type email · existence probe',
        run: async (cfg) => {
          // cfg.email is set for the Login flow (the main QA account). The
          // trial-upload flow sends no email, so it falls back to the account the
          // signup flow just created (persisted on this product origin) — logging
          // in to that SAME account rather than making a new one.
          const email = requireField(cfg.email || storedSignupEmail(), 'email')
          const input = await waitFor(() => visibleByPlaceholder('Email'), { timeout: 15000 })
          await humanType(input, email)
          return email
        },
      },
      {
        title: 'Pass bot check',
        hint: 'Cloudflare Turnstile → Continue',
        run: async () => {
          const passwordShown = () => passwordInput()
          let btn
          try {
            btn = await waitFor(submitButton, { timeout: 35000 })
          } catch {
            throw new Error(`The email "Continue" button never enabled (Turnstile / email probe). Visible buttons: ${visibleButtonsDump() || 'none'}`)
          }
          // Click; if the password step doesn't appear, retry and fall back to Enter.
          for (let attempt = 0; attempt < 3; attempt++) {
            const b = submitButton() || btn
            if (typeof b.focus === 'function') b.focus()
            b.click()
            try {
              await waitFor(passwordShown, { timeout: 6000 })
              return 'continued to password'
            } catch {
              pressEnter(visibleByPlaceholder('Email'))
            }
            try {
              await waitFor(passwordShown, { timeout: 4000 })
              return 'continued to password'
            } catch {
              /* retry the loop */
            }
          }
          throw new Error(`Clicked Continue, but the password field never appeared. Visible buttons: ${visibleButtonsDump() || 'none'}`)
        },
      },
      {
        title: 'Enter password',
        hint: 'type password',
        run: async (cfg) => {
          const pw = requireField(cfg.password, 'password')
          const input = await waitFor(passwordInput, { timeout: 10000 })
          await humanType(input, pw)
          return 'password entered'
        },
      },
      {
        title: 'Sign in',
        hint: 'submit → /v4/signin',
        run: async () => {
          const btn = await waitFor(submitButton, { timeout: 12000 })
          if (typeof btn.focus === 'function') btn.focus()
          btn.click()
          return 'submitted'
        },
      },
      {
        title: 'Verify signed in',
        hint: 'land on /access',
        run: async () => {
          const end = Date.now() + 25000
          while (Date.now() < end) {
            const path = location.pathname
            if (/\/access|\/quick-user-payment/.test(path)) return `signed in → ${path}`
            if (/newMobile-verification|newVerification-code|email-verification/.test(path)) {
              throw new Error(`Hit a verification wall (${path}) — use a QA account without MFA / with a verified email.`)
            }
            if (pageHasText(/Incorrect password|Oops! Something went wrong|couldn't verify your email/i)) {
              throw new Error('Login rejected — check the credentials.')
            }
            if (pageHasText(/Ready to watch new content added by your projectors/i)) return 'signed in (welcome shown)'
            await sleep(500)
          }
          throw new Error(`No success signal within 25s (still on ${location.pathname}).`)
        },
      },
]

const FLOWS = {
  login: { title: 'Login', steps: loginSteps },

  signup: {
    title: 'Signup',
    steps: [
      {
        title: 'Open sign-up page',
        hint: 'wait for form',
        run: async () => {
          await waitFor(() => visibleInputByType('email'), { timeout: 25000 })
          return `loaded ${location.pathname}`
        },
      },
      {
        title: 'Enter new email',
        hint: 'random @yopmail.com · existence check',
        run: async () => {
          const input = await waitFor(() => visibleInputByType('email'), { timeout: 10000 })
          // The app probes /signupEmail (debounced) and only treats an address as
          // usable when it comes back free: it reveals the name/reCAPTCHA fields
          // (accountExists=false) and, on a taken address, shows the "existing
          // projector account" notice. Wait on those real signals, not a timer.
          const taken = () => pageHasText(/existing projector account|login instead/i)
          const freeSignal = () => nameFieldInputs().length >= 2 || !!submitBy(/continue/i, /login|sign ?in|with/i)
          for (let attempt = 0; attempt < 6; attempt++) {
            const email = randomYopmail()
            await humanType(input, email)
            const settle = Date.now() + 9000
            let decided = ''
            while (Date.now() < settle) {
              if (taken()) { decided = 'taken'; break }
              if (freeSignal()) { decided = 'free'; break }
              await sleep(300)
            }
            if (decided === 'taken') { qlog('email already registered, regenerating:', email); continue }
            qlog(`email accepted (${decided || 'no-taken-signal'}):`, email)
            // Remember for the trial-upload hand-off; only PERSISTED once the
            // account is fully created (see "Verify & finish").
            lastSignupEmail = email
            return email
          }
          throw new Error('Could not confirm a free @yopmail.com address — the signup existence probe never settled.')
        },
      },
      {
        title: 'Agree & continue',
        hint: 'name (if here) · reCAPTCHA → next',
        run: async (cfg) => {
          // Name fields (First/Last) may be on page 1 (and required to enable the
          // button) — wait briefly and fill them if present. If they're on page 2
          // instead, the next step fills them there.
          const names = await waitFor(() => { const f = nameFieldInputs(); return f.length >= 2 ? f : null }, { timeout: 9000 }).catch(() => null)
          if (names) {
            await humanType(names[0], cfg.firstName || 'QA')
            await humanType(names[1], cfg.lastName || 'Tester')
          }
          let btn
          try {
            btn = await waitFor(() => submitBy(/agree|continue/i, /login|sign ?in|with/i), { timeout: 35000 })
          } catch {
            throw new Error(`"Agree and Continue" never enabled (needs name + reCAPTCHA). Visible buttons: ${visibleButtonsDump() || 'none'}`)
          }
          btn.click()
          try {
            await waitFor(() => passwordInput() || nameFieldInputs().length >= 2, { timeout: 12000 })
          } catch {
            throw new Error(`Next page didn't appear after Continue (now on ${location.pathname}).`)
          }
          return names ? 'name + continue' : 'continued'
        },
      },
      {
        title: 'Fill details',
        hint: 'name + phone + password (one page)',
        run: async (cfg) => {
          // This page carries First/Last name, the phone field, AND the password.
          const names = nameFieldInputs()
          if (names.length >= 2) {
            await humanType(names[0], cfg.firstName || 'QA')
            await humanType(names[1], cfg.lastName || 'Tester')
          }
          // Phone (react-phone-number-input) lives on this same page. React is a
          // controlled input, so a value occasionally doesn't "stick" on the
          // first pass — verify and retype once if it came back empty.
          const phone = cfg.phone || ''
          const tel = document.querySelector('input.PhoneInputInput') || visibleInputByType('tel')
          if (tel && phone) {
            await fillPhone(tel, phone)
            if (!tel.value) { await sleep(250); await fillPhone(tel, phone) }
          }
          const pw = await waitFor(passwordInput, { timeout: 10000 })
          const pwText = requireField(cfg.password, 'password')
          await humanType(pw, pwText)
          if ((pw.value || '').length !== pwText.length) { await sleep(200); await humanType(pw, pwText) }
          const bits = []
          if (names.length >= 2) bits.push('name')
          if (tel) bits.push(tel.value ? 'phone' : 'phone(EMPTY!)')
          bits.push(`password(${(pw.value || '').length})`)
          return bits.join(' + ')
        },
      },
      {
        title: 'Create account',
        hint: 'submit → sends OTP',
        run: async (cfg) => {
          const phoneField = () => document.querySelector('input.PhoneInputInput') || visibleInputByType('tel')
          // We're on the details page for as long as the password field is shown.
          // "Advanced" = that field is gone (OTP / verification screen) or the
          // code boxes / verification copy appeared. The phone field can't be the
          // signal — it lives on THIS page too (the old bug that made this step
          // think it had progressed when it hadn't).
          const advanced = () => otpBoxes().length >= 4
            || !passwordInput()
            || pageHasText(/verification code|code we (?:sent|texted)|enter the (?:6|six)|didn'?t get a code|resend code/i)

          let lastErr = ''
          for (let attempt = 0; attempt < 3; attempt++) {
            // Re-assert phone + password before every attempt (a dropped value is
            // the usual reason the submit silently does nothing).
            const telPre = phoneField()
            if (telPre && !telPre.value && cfg.phone) await fillPhone(telPre, cfg.phone)
            const pwPre = passwordInput()
            if (pwPre && !pwPre.value && cfg.password) await humanType(pwPre, cfg.password)

            let btn
            try {
              btn = await waitFor(() => submitBy(/agree|continue|signing up|send|get code|verify/i, /login|resend|change number|back|cancel/i), { timeout: 12000 })
            } catch {
              throw new Error(`Submit never enabled. password: ${pwPre ? `${(pwPre.value || '').length} chars` : 'no field'}, phone: ${telPre ? `"${telPre.value}"` : 'no field'}. Visible buttons: ${visibleButtonsDump() || 'none'}`)
            }
            qlog(`click create (attempt ${attempt + 1}) →`, describe(btn))
            btn.click()

            // Wait for progress OR a surfaced validation/Firebase error.
            try {
              await waitFor(() => advanced() || pageErrorText(), { timeout: 12000 })
            } catch { /* nothing happened — fall through to retry */ }

            if (advanced()) { lastErr = ''; break }
            lastErr = pageErrorText()
            if (lastErr) qlog('⚠ app error after submit:', lastErr)
            // A transient reCAPTCHA failure often clears on a second send, so
            // pause and retry rather than bailing immediately.
            await sleep(1400)
          }

          if (!advanced()) {
            const recaptcha = /invalid[- ]app[- ]credential|recaptcha/i.test(lastErr)
            const hint = recaptcha
              ? 'Firebase reCAPTCHA failed (INVALID_APP_CREDENTIAL) — this is the cross-origin iframe. Run signup on the top-level projector.app domain, or enable the Firebase test-number bypass.'
              : FIREBASE_HINT
            throw new Error(`${lastErr ? `App said: "${lastErr}". ` : ''}Submit didn't advance (still on ${location.pathname}). ${hint}`)
          }

          // On the details page the same submit also sends the OTP and swaps in
          // the 6 boxes. If instead we landed on a separate verification screen,
          // (re)send there.
          if (otpBoxes().length < 4) {
            const tel = phoneField()
            if (tel && !tel.value && cfg.phone) await fillPhone(tel, cfg.phone)
            const send = submitBy(/send|get code|verify|continue|agree/i, /resend|change|back|cancel/i)
            if (send) { qlog('click send →', describe(send)); send.click() }
            try {
              await waitFor(() => otpBoxes().length >= 4, { timeout: 20000 })
            } catch {
              const e2 = pageErrorText()
              throw new Error(`${e2 ? `App said: "${e2}". ` : ''}OTP boxes never appeared (now on ${location.pathname}). ${FIREBASE_HINT}`)
            }
          }
          return 'code sent'
        },
      },
      {
        title: 'Enter OTP',
        hint: '6-digit code',
        run: async (cfg) => {
          const otp = String(requireField(cfg.otp, 'otp'))
          const boxes = await waitFor(() => { const b = otpBoxes(); return b.length >= otp.length ? b : null }, { timeout: 15000 })
          qlog(`OTP: found ${boxes.length} boxes, entering "${otp}"`)
          for (let i = 0; i < otp.length; i++) {
            boxes[i].focus()
            setNativeValue(boxes[i], otp[i])
            await sleep(60)
          }
          return 'code entered'
        },
      },
      {
        title: 'Verify & finish',
        hint: 'confirm → /avatar',
        run: async () => {
          const btn = await waitFor(() => submitBy(/continue|verify/i, /resend|change|back|cancel|log ?out/i), { timeout: 12000 })
          btn.click()
          const end = Date.now() + 30000
          while (Date.now() < end) {
            const path = location.pathname
            if (/\/avatar|\/plans|\/access/.test(path)) {
              // The account now exists — persist its email so the trial-upload
              // test can log in to THIS same account (one account per Run all).
              rememberSignupEmail(lastSignupEmail)
              return `signed up → ${path}`
            }
            if (pageHasText(/incorrect code|that code expired|code has expired|invalid/i)) {
              throw new Error('OTP rejected — the code was wrong or expired.')
            }
            await sleep(500)
          }
          throw new Error(`No success after OTP within 30s (still on ${location.pathname}).`)
        },
      },
      {
        title: 'Choose avatar',
        hint: 'pick avatar → Continue',
        run: async () => {
          await waitFor(() => /\/avatar/.test(location.pathname) || pageHasText(/Choose Your Avatar/i), { timeout: 20000 })
          // Pick one avatar from the collection. A default (avatar11) is
          // auto-selected on mount, but clicking a tile is the real human action
          // and guarantees the preview + "changed" flags that enable Continue.
          const tile = Array.from(document.querySelectorAll('button')).find(
            (b) => isVisible(b) && b.querySelector('img[alt^="Avatar" i]'),
          )
          if (tile) { qlog('click avatar tile →', describe(tile)); tile.click(); await sleep(900) }
          // Continue needs the account's first/last name (hidden here but still
          // required); they come back from the signup, so it should enable.
          let btn
          try {
            btn = await waitFor(() => submitBy(/continue/i, /skip|back|resend|change|log ?out/i), { timeout: 20000 })
          } catch {
            throw new Error(`Avatar "Continue" never enabled (the account may be missing a name). Visible buttons: ${visibleButtonsDump() || 'none'}`)
          }
          qlog('click →', describe(btn)); btn.click()
          try {
            await waitFor(() => !/\/avatar/.test(location.pathname), { timeout: 20000 })
          } catch {
            throw new Error(`Still on the avatar page after Continue (${location.pathname}).`)
          }
          return `avatar set → ${location.pathname}`
        },
      },
      {
        title: 'Pick occasion',
        hint: 'select → Continue',
        run: async () => {
          await waitFor(() => /\/choose-occasion/.test(location.pathname) || pageHasText(/special occasion|choose your/i), { timeout: 20000 })
          // Select one occasion card (optional for the flow, but human-like).
          const occ = Array.from(document.querySelectorAll('button, [role="button"]')).find(
            (b) => isVisible(b) && /occasionButton/i.test((b.className || '').toString()),
          )
          if (occ) { qlog('click occasion →', describe(occ)); occ.click(); await sleep(500) }
          // Continue only mounts after the grid fade-in (~2.9s), so allow time.
          let btn
          try {
            btn = await waitFor(() => submitBy(/continue/i, /skip|back/i), { timeout: 14000 })
          } catch {
            throw new Error(`Occasion "Continue" never appeared. Visible buttons: ${visibleButtonsDump() || 'none'}`)
          }
          qlog('click →', describe(btn)); btn.click()
          try {
            await waitFor(() => !/\/choose-occasion/.test(location.pathname), { timeout: 20000 })
          } catch {
            throw new Error(`Still on the occasion page after Continue (${location.pathname}).`)
          }
          return `occasion set → ${location.pathname}`
        },
      },
      {
        title: 'Skip upload',
        hint: 'upload blocked → No Media To Upload',
        run: async () => {
          // The upload grid. Free/new accounts are intentionally BLOCKED from
          // uploading (clicking an upload option raises an upgrade popup), so we
          // never upload — we take the "No Media To Upload" exit to the device
          // screen. A blocker popup, if one is up, is expected and fine.
          await waitFor(() => /\/gift-content/.test(location.pathname) || clickableByText(/no media to upload/i), { timeout: 25000 })
          const noMedia = await waitFor(() => clickableByText(/no media to upload/i), { timeout: 15000 })
          qlog('click →', describe(noMedia)); noMedia.click()
          try {
            await waitFor(() => /\/ecosystem|\/inviteuser|\/access/.test(location.pathname), { timeout: 20000 })
          } catch {
            throw new Error(`Still on the upload page after "No Media To Upload" (${location.pathname}).`)
          }
          return `skipped upload → ${location.pathname}`
        },
      },
      {
        title: 'Choose device · Next',
        hint: 'device screen → Next',
        run: async () => {
          if (/\/inviteuser|\/access/.test(location.pathname)) return `already at ${location.pathname}`
          await waitFor(() => /\/ecosystem/.test(location.pathname) || clickableByText(/^\s*next\s*$/i, /watch|view your|computer|download|projector/i), { timeout: 20000 })
          const next = await waitFor(() => clickableByText(/^\s*next\s*$/i, /watch|view your|computer|download|projector/i), { timeout: 15000 })
          qlog('click →', describe(next)); next.click()
          try {
            await waitFor(() => /\/inviteuser|\/access/.test(location.pathname), { timeout: 20000 })
          } catch {
            throw new Error(`Still on the device page after Next (${location.pathname}).`)
          }
          return `next → ${location.pathname}`
        },
      },
      {
        title: 'Skip invite → done',
        hint: 'invite → Skip → /access',
        run: async () => {
          if (/\/access/.test(location.pathname)) return 'signed in → /access'
          await waitFor(() => /\/inviteuser/.test(location.pathname) || clickableByText(/skip/i), { timeout: 20000 })
          const skip = await waitFor(() => clickableByText(/skip/i), { timeout: 15000 })
          qlog('click →', describe(skip)); skip.click()
          const endT = Date.now() + 25000
          while (Date.now() < endT) {
            if (/\/access/.test(location.pathname)) return 'onboarded → /access'
            await sleep(500)
          }
          return `reached ${location.pathname}`
        },
      },
    ],
  },

  // Single video upload — logs in, opens the dashboard header uploader, and
  // exercises every field of the upload modal (title, category + category
  // CREATION, visibility, description) before publishing a real ~2MB clip.
  'upload-video': {
    title: 'Upload video',
    steps: [
      ...loginSteps,
      goToDashboardStep,
      openUploaderStep,
      {
        title: 'Choose video',
        hint: 'set a ~2MB .mp4',
        run: async () => {
          const input = await waitFor(uploadInput, { timeout: 10000 })
          const file = await makeVideoFile(2 * 1024 * 1024, 'qa-video')
          setFilesOnInput(input, [file])
          if (isNewUIRun()) {
            // New UI: "Choose Local Files" uploads straight to the pipeline — no
            // modal. The UploadPanel takes over; the modal-field steps below are
            // n/a and the Verify step confirms completion.
            return `added ${file.name} · ${(file.size / 1048576).toFixed(1)}MB (direct upload)`
          }
          // Classic: single file → stays in VideoUploadModal, title auto-fills.
          await waitFor(() => /upload video details/i.test(bodyText()) || visibleByPlaceholder('Enter an engaging title...'), { timeout: 15000 })
          return `added ${file.name} · ${(file.size / 1048576).toFixed(1)}MB`
        },
      },
      {
        title: 'Set title',
        hint: 'auto-fill → custom title',
        run: async () => {
          if (isNewUIRun()) return 'n/a · new UI uploads directly (no modal)'
          const input = await waitFor(() => visibleByPlaceholder('Enter an engaging title...'), { timeout: 10000 })
          const auto = (input.value || '').trim()
          const title = `QA video ${new Date().toISOString().slice(0, 19).replace('T', ' ')}`
          await humanType(input, title)
          if ((input.value || '').trim() !== title) throw new Error(`Title field did not accept input (value="${input.value}").`)
          return `auto="${auto}" → "${title}"`
        },
      },
      {
        title: 'Create category',
        hint: 'Category → Create New → Create Custom',
        run: async () => {
          if (isNewUIRun()) return 'n/a · new UI uploads directly (no modal)'
          const sel = await waitFor(() => muiSelectByLabel('Category'), { timeout: 10000 })
          const before = (sel.textContent || '').trim()
          qlog('open Category →', describe(sel)); openMuiSelect(sel)
          const createItem = await waitFor(() => optionByText(/create new category/i), { timeout: 8000 })
          qlog('click Create New Category →', describe(createItem)); createItem.click()
          // CreateCategory dialog opens in "Choose Preset" mode — switch to custom.
          const customTab = await waitFor(() => elByText(/^create custom$/i), { timeout: 8000 })
          qlog('click Create Custom tab →', describe(customTab)); customTab.click()
          const nameInput = await waitFor(() => visibleByPlaceholder('Enter category title'), { timeout: 8000 })
          const catName = `QA Cat ${Date.now().toString(36)}`
          await humanType(nameInput, catName)
          const createBtn = await waitFor(() => clickableByText(/create category/i, /custom|new category/i), { timeout: 8000 })
          qlog('click Create Category →', describe(createBtn)); createBtn.click()
          // Dialog closes and the new category becomes the modal's selection.
          try {
            await waitFor(() => !visibleByPlaceholder('Enter category title'), { timeout: 15000 })
          } catch {
            throw new Error('Create-category dialog did not close (category may not have saved).')
          }
          await sleep(800)
          const now = (muiSelectByLabel('Category')?.textContent || '').trim()
          return `created "${catName}" (was "${before || '—'}", now "${now || '—'}")`
        },
      },
      {
        title: 'Set visibility',
        hint: 'All Viewers → Private',
        run: async () => {
          if (isNewUIRun()) return 'n/a · new UI uploads directly (no modal)'
          const sel = await waitFor(() => muiSelectByLabel('Visibility'), { timeout: 8000 })
          qlog('open Visibility →', describe(sel)); openMuiSelect(sel)
          const priv = await waitFor(() => optionByText(/^\s*private\s*$/i), { timeout: 8000 })
          priv.click()
          await sleep(400)
          const now = (muiSelectByLabel('Visibility')?.textContent || '').trim()
          return `visibility → ${now || 'set'}`
        },
      },
      {
        title: 'Add description',
        hint: 'expand options → type',
        run: async () => {
          if (isNewUIRun()) return 'n/a · new UI uploads directly (no modal)'
          // The description is a multiline TextField → a <textarea>.
          const find = () => Array.from(document.querySelectorAll('textarea[placeholder="Add a compelling description for your video..."], input[placeholder="Add a compelling description for your video..."]')).find(isVisible) || null
          let desc = find()
          if (!desc) {
            const toggle = elByText(/additional options/i)
            if (toggle) { qlog('expand Additional Options →', describe(toggle)); toggle.click() }
            desc = await waitFor(find, { timeout: 8000 })
          }
          const text = 'Automated QA upload — please ignore.'
          await humanType(desc, text)
          if (!(desc.value || '').includes('QA upload')) throw new Error('Description field did not accept input.')
          return 'description set'
        },
      },
      {
        title: 'Publish',
        hint: 'Publish Video → modal closes',
        run: async () => {
          if (isNewUIRun()) return 'n/a · upload auto-started (no modal)'
          // Publish enables once title + category are set and thumbnail
          // generation finishes; allow generous time for the latter.
          const btn = await waitFor(() => clickableByText(/publish video/i, /publishing/i), { timeout: 90000 })
          qlog('click Publish →', describe(btn)); btn.click()
          const closed = await ensureUploadModalClosed()
          return `publishing · ${closed}`
        },
      },
      makeVerifyStep(1),
    ],
  },

  // Multiple video upload — selecting several files switches the modal to the
  // batch ("Review & publish") view; each video needs a title (auto from the
  // filename), which we override, then Publish N videos.
  'upload-videos': {
    title: 'Upload videos (×3)',
    steps: [
      ...loginSteps,
      goToDashboardStep,
      openUploaderStep,
      {
        title: 'Choose 3 videos',
        hint: 'set 3 × ~2MB .mp4',
        run: async () => {
          const input = await waitFor(uploadInput, { timeout: 10000 })
          const files = []
          for (let i = 0; i < 3; i++) files.push(await makeVideoFile(2 * 1024 * 1024, `qa-video-${i + 1}`))
          setFilesOnInput(input, files)
          if (isNewUIRun()) {
            // New UI: all 3 go straight to the pipeline (no batch modal).
            return `added ${files.length} videos (direct upload)`
          }
          // Classic: >1 file → the multiple-video modal.
          await waitFor(multiModalOpen, { timeout: 15000 })
          return `added ${files.length} videos`
        },
      },
      {
        title: 'Title each video',
        hint: 'set 3 distinct titles',
        run: async () => {
          if (isNewUIRun()) return 'n/a · new UI uploads directly (no modal)'
          const boxes = await waitFor(() => { const b = multiTitleInputs(); return b.length >= 3 ? b : null }, { timeout: 12000 })
          for (let i = 0; i < 3; i++) {
            await humanType(boxes[i], `QA batch #${i + 1} ${Date.now().toString(36)}`)
          }
          const empty = boxes.slice(0, 3).filter((b) => !(b.value || '').trim()).length
          if (empty) throw new Error(`${empty} title field(s) stayed empty.`)
          return `titled ${Math.min(3, boxes.length)} videos`
        },
      },
      {
        title: 'Publish all',
        hint: 'Publish videos',
        run: async () => {
          if (isNewUIRun()) return 'n/a · upload auto-started (no modal)'
          const btn = await waitFor(multiPublishButton, { timeout: 90000 })
          qlog('click Publish all →', describe(btn)); btn.click()
          await waitFor(() => !multiModalOpen(), { timeout: 15000 }).catch(() => {})
          return multiModalOpen() ? 'publishing 3 · modal still open' : 'publishing 3 · modal closed'
        },
      },
      makeVerifyStep(3),
    ],
  },
}

// Trial upload (blocked) — LOGS IN to the account the signup flow just created
// (its email is persisted on this product origin; password is the same creds), so
// a Run all makes ONE account total, shared with the Signup test. Then it verifies
// the app BLOCKS an upload attempt: a no-plan user must hit the upgrade popup (or a
// plans/purchase redirect) and nothing may reach the upload pipeline. PASSES when
// blocked, FAILS if the upload is allowed. Run it after a successful Signup (or Run
// all); on its own with no signup account yet, the login step reports no email.
FLOWS['trial-upload'] = {
  title: 'Trial upload blocked',
  steps: [
    ...loginSteps,
    goToDashboardStep,
    {
      title: 'Attempt upload',
      hint: 'no-plan account must be blocked',
      run: async () => {
        // BOTH UIs: click the dashboard's "Choose Local Files" tile — the SAME
        // button a real user clicks. Its handler does `await isUploadLimitBlocked()`,
        // which hits the API (autoSignIn → totalContentUploaded); that async check
        // is the brief "validating" delay, after which a no-plan user gets the
        // upgrade popup BEFORE any file picker opens.
        //   NOTE: the classic header "Upload → Upload Videos" menu is a DIFFERENT,
        //   UNGUARDED path (handleVideoUpload → onVideoUpload, no plan check) — a
        //   real product gap, tracked separately. We deliberately test the guarded
        //   tile here so this mirrors manual behaviour on both designs.
        // Trap any file-input click so that, if the app WRONGLY lets it through, no
        // OS dialog opens and we record the upload as allowed (a failure).
        let pickerReached = false
        const trap = (ev) => {
          const el = ev.target
          if (el && el.tagName === 'INPUT' && el.type === 'file') {
            pickerReached = true
            try { ev.preventDefault(); ev.stopImmediatePropagation() } catch (_) { /* noop */ }
          }
        }
        document.addEventListener('click', trap, true)
        try {
          const tile = await waitFor(() => clickableByText(/choose local files/i), { timeout: 20000 })
          qlog('click Choose Local Files →', describe(tile)); tile.click()
          // Wait through the async isUploadLimitBlocked() API check for either the
          // upgrade popup (correct) or the file picker being reached (a failure).
          await waitFor(() => upgradeBlockShown() || pickerReached, { timeout: 15000 }).catch(() => {})
        } finally {
          document.removeEventListener('click', trap, true)
        }
        const blocked = upgradeBlockShown()
        if (blocked) return `blocked · ${blocked}`
        if (pickerReached) throw new Error('Upload ALLOWED — the no-plan account reached the file picker with no upgrade popup.')
        throw new Error('No upgrade / block popup appeared when a no-plan user clicked "Choose Local Files".')
      },
    },
    {
      title: 'Verify blocked',
      hint: 'no upload reached the pipeline',
      run: async () => {
        // Belt-and-braces: confirm nothing slipped into the global UploadPanel.
        await sleep(1500)
        if (/Uploaded \d+ file|[1-9]\d* completed|Uploading \d+ file/i.test(bodyText())) {
          throw new Error('An upload started despite the no-plan block — content reached the upload pipeline.')
        }
        return 'no upload started (correctly blocked)'
      },
    },
  ],
}

// Play video — logs in (layne, who has content), opens /profile, clicks the
// latest video tile, plays it, and confirms the <video> is actually playing. The
// tile opens an in-place VideoModal (new UI) or the /video/:id details page
// (classic); both have a "Play" button → /player/:id/play (native autoplay).
FLOWS['play-video'] = {
  title: 'Play video',
  steps: [
    ...loginSteps,
    goToProfileStep,
    {
      title: 'Open latest video',
      hint: 'click newest tile → details',
      run: async () => {
        const tile = await waitFor(videoTile, { timeout: 20000 })
        const href = tile.getAttribute('href') || ''
        qlog('click latest video tile →', describe(tile), href)
        tile.click()
        // Wait for the Play button (modal or details page) or a /video/ nav.
        await waitFor(() => clickableByText(/^\s*(play|resume)\s*$/i, /playlist|auto\s*play|replay|watch\s*list/i) || /\/video\//.test(location.pathname), { timeout: 15000 })
        return `opened ${href || location.pathname}`
      },
    },
    {
      title: 'Play video',
      hint: 'Play → /player → playing',
      run: async () => {
        const playBtn = await waitFor(() => clickableByText(/^\s*(play|resume)\s*$/i, /playlist|auto\s*play|replay|watch\s*list/i), { timeout: 15000 })
        qlog('click Play →', describe(playBtn)); playBtn.click()
        await waitFor(() => /\/player\//.test(location.pathname), { timeout: 15000 })
        const v = await waitFor(() => { const el = document.querySelector('video'); return (el && el.readyState >= 2) ? el : null }, { timeout: 30000 })
        // Confirm real playback (time advancing), not just a loaded poster frame.
        const t1 = v.currentTime
        await sleep(1600)
        if (!v.paused && v.currentTime > t1) return `playing · ${location.pathname} (t=${v.currentTime.toFixed(1)}s)`
        // Autoplay can be blocked until a gesture — nudge it muted and recheck.
        try { v.muted = true; await v.play() } catch (_) { /* noop */ }
        await sleep(1200)
        if (!v.paused && v.currentTime > 0) return `playing (nudged) · t=${v.currentTime.toFixed(1)}s`
        throw new Error(`Reached ${location.pathname} but the video did not start playing (paused=${v.paused}, t=${v.currentTime.toFixed(2)}).`)
      },
    },
  ],
}

/* ---------- controller messaging ---------- */
export function initQaBridge() {
  let params
  try { params = new URLSearchParams(window.location.search) } catch { return }
  if (params.get('qa_bridge') !== '1') return

  try { window.__pjQaBridge = `active ${QA_BRIDGE_VERSION}` } catch { /* noop */ }
  // eslint-disable-next-line no-console
  console.info('[qa-bridge]', QA_BRIDGE_VERSION, 'active on', window.location.pathname)

  // Fresh session every run — before React boots and reads the token.
  clearAuthForFreshSession()

  const controller = window.opener && window.opener !== window ? window.opener : window.parent
  if (!controller || controller === window) return

  const post = (msg) => { try { controller.postMessage({ __pjqa: 1, ...msg }, '*') } catch { /* gone */ } }
  let busy = false

  async function runFlow(id, config) {
    const flow = FLOWS[id]
    if (!flow) { post({ type: 'run-end', ok: false, summary: `Unknown test "${id}"` }); return }
    qlog(`══════ RUN "${id}"  config keys: ${Object.keys(config).join(', ')} ══════`)
    post({ type: 'run-start', test: id, steps: flow.steps.map((s) => ({ title: s.title, hint: s.hint })) })
    let failedAt = -1
    for (let i = 0; i < flow.steps.length; i++) {
      post({ type: 'step', index: i, status: 'running' })
      qlog(`─────── STEP ${i + 1}/${flow.steps.length}: ${flow.steps[i].title} ───────`)
      snap(`before "${flow.steps[i].title}"`)
      const t0 = Date.now()
      try {
        const detail = await flow.steps[i].run(config)
        qlog(`✓ STEP ${i + 1} done: ${flow.steps[i].title} — ${typeof detail === 'string' ? detail : ''} (${Date.now() - t0}ms)`)
        post({ type: 'step', index: i, status: 'done', detail: typeof detail === 'string' ? detail : undefined, ms: Date.now() - t0 })
      } catch (err) {
        qlog(`✗ STEP ${i + 1} FAILED: ${flow.steps[i].title} → ${(err && err.message) || err}`)
        snap(`AT FAILURE of "${flow.steps[i].title}"`)
        post({ type: 'step', index: i, status: 'failed', error: (err && err.message) || String(err), ms: Date.now() - t0 })
        failedAt = i
        break
      }
    }
    post({
      type: 'run-end',
      ok: failedAt === -1,
      summary: failedAt === -1 ? `All ${flow.steps.length} steps passed` : `Failed at step ${failedAt + 1} — ${flow.steps[failedAt].title}`,
    })
  }

  window.addEventListener('message', (e) => {
    if (!isAllowedOrigin(e.origin)) return
    const d = e.data
    if (!d || !d.__pjqa) return
    if (d.type === 'run' && !busy) {
      busy = true
      Promise.resolve(runFlow(d.test, d.config || {})).finally(() => { busy = false })
    }
  })

  // Announce readiness (and re-announce shortly after, in case the controller
  // attaches its listener a beat later).
  const flows = Object.entries(FLOWS).map(([id, f]) => ({ id, title: f.title, outline: f.steps.map((s) => ({ title: s.title, hint: s.hint })) }))
  post({ type: 'ready', flows })
  setTimeout(() => post({ type: 'ready', flows }), 800)
}
